import React, { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import ReportGoodNewsCarousel from "./ReportGoodNewsCarousel";
import {
  BookIcon,
  DangerIcon,
  DoneIcon,
  ExtensionIcon,
  FlagIcon,
  Heart2Icon,
  InfoCircleIcon,
  ProfileTickIcon,
  StarRoundIcon,
  ThumbUpIcon,
  XIcon,
} from "../assets/icons";

const competencyTotals = { competencies: 3, indicators: 158 };

let competencyProgress = [];

let featuredCompetencies = [];

let competencyCarouselData = {};

let competencyTopIndicators = {};

let reflections = [];

let otherMetrics = [];

function SectionHeading({ Icon, title, actionLabel }) {
  return (
    <View style={styles.sectionHeading}>
      <View style={styles.iconTile}>
        <Icon width={17} height={17} customColor="#5423B5" stroke="#5423B5" />
      </View>
      <Text style={styles.sectionTitle}>{title}</Text>
      {!!actionLabel && (
        <View style={styles.actionBadge}>
          <Text style={styles.actionLabel}>{actionLabel}</Text>
        </View>
      )}
    </View>
  );
}

function SummaryTotalRow({ Icon, label, value }) {
  return (
    <View style={styles.summaryRow}>
      <View style={styles.summaryIcon}>
        <Icon width={18} height={18} stroke="#FFFFFF" customColor="#FFFFFF" />
      </View>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={styles.summaryValue}>{value}</Text>
    </View>
  );
}

function ProgressStatusRow({ item, isLast }) {
  return (
    <View style={[styles.progressRow, isLast && styles.lastProgressRow]}>
      <View
        style={[styles.progressStatusIcon, { backgroundColor: item.color }]}
      >
        {item.key === "passed" ? (
          <DoneIcon width={20} height={20} customColor="#FFFFFF" />
        ) : item.key === "failed" ? (
          <XIcon width={20} height={20} customColor="#FFFFFF" />
        ) : (
          <InfoCircleIcon width={21} height={20} customColor="#FFFFFF" />
        )}
      </View>
      <View style={styles.progressCopy}>
        <Text style={styles.progressLabel}>{item.label}</Text>
        <Text style={styles.progressCount}>
          {item.count} dari {competencyTotals.indicators}
          <Text style={styles.progressPercentage}> • {item.percentage}%</Text>
        </Text>
      </View>
      <View style={styles.progressTrack}>
        <View
          style={[
            styles.progressFill,
            { width: `${item.percentage}%`, backgroundColor: item.color },
          ]}
        />
      </View>
    </View>
  );
}

function CompetencyScoreRow({ item }) {
  return (
    <View style={styles.competencyScoreRow}>
      <View style={styles.competencyCopy}>
        <Text style={styles.competencyName}>{item.name}</Text>
        <Text style={styles.competencyCount}>{item.indicators} Indikator</Text>
      </View>
      <View style={styles.competencyScoreColumn}>
        <Text style={styles.competencyPercentage}>{item.percentage}%</Text>
        <View style={styles.competencyTrack}>
          <View
            style={[styles.competencyFill, { width: `${item.percentage}%` }]}
          />
        </View>
      </View>
    </View>
  );
}

function CompetencyTopIndicatorRow({ item, isLast, isAttention }) {
  return (
    <View
      style={[styles.topIndicatorRow, isLast && styles.lastTopIndicatorRow]}
    >
      <View style={styles.topIndicatorChips}>
        <View style={styles.topCategoryChip}>
          <Heart2Icon customColor="#999999" />
          <Text style={styles.topCategoryText}>{item.category}</Text>
        </View>
        <View
          style={[
            styles.topStatusChip,
            isAttention ? styles.supportStatusChip : styles.bestStatusChip,
          ]}
        >
          <FlagIcon
            width={13}
            height={13}
            customColor={isAttention ? "#D84343" : "#48B920"}
          />
          <Text
            style={[
              styles.topStatusText,
              isAttention && styles.supportStatusText,
            ]}
          >
            {item.label}
          </Text>
        </View>
      </View>
      <Text style={styles.topIndicatorDescription}>{item.name}</Text>
    </View>
  );
}

function CompetencyTopIndicators() {
  const [mode, setMode] = useState("positive");
  const isAttention = mode === "support";
  const indicators = competencyTopIndicators[mode];
  if (!indicators) {
    return <></>;
  }

  return (
    <View style={styles.card}>
      <SectionHeading
        Icon={ExtensionIcon}
        title="TOP 3 Indikator"
        actionLabel="Lihat Semua"
      />
      <View style={styles.competencySegments}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ selected: !isAttention }}
          onPress={() => setMode("positive")}
          style={[
            styles.competencySegment,
            !isAttention && styles.bestSegmentActive,
          ]}
        >
          <ThumbUpIcon
            width={22}
            height={22}
            customColor={!isAttention ? "#48B920" : "#151515"}
          />
          <Text
            style={[
              styles.competencySegmentText,
              !isAttention && styles.bestSegmentText,
            ]}
          >
            Indikator{"\n"}Terbaik
          </Text>
          {!isAttention && (
            <View style={[styles.segmentUnderline, styles.bestUnderline]} />
          )}
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ selected: isAttention }}
          onPress={() => setMode("support")}
          style={[
            styles.competencySegment,
            isAttention && styles.supportSegmentActive,
          ]}
        >
          <DangerIcon
            width={22}
            height={22}
            fill={isAttention ? "#D84343" : "#151515"}
          />
          <Text
            style={[
              styles.competencySegmentText,
              isAttention && styles.supportSegmentText,
            ]}
          >
            Perlu{"\n"}Bimbingan
          </Text>
          {isAttention && (
            <View style={[styles.segmentUnderline, styles.supportUnderline]} />
          )}
        </Pressable>
      </View>
      {indicators.map((item, index) => (
        <CompetencyTopIndicatorRow
          key={`${item.category}-${index}`}
          item={item}
          isAttention={isAttention}
          isLast={index === indicators.length - 1}
        />
      ))}
    </View>
  );
}

function CompetencyContent({ statistic, period }) {
  // processing statistic data
  if (statistic) {
    competencyTotals.competencies = statistic?.materials?.length;
    competencyTotals.indicators = 0;
    let passedIndicators = 0;
    let failedIndicators = 0;
    let pendingIndicators = 0;
    let indicators = [];
    competencyProgress = [
      {
        key: "passed",
        label: "Indikator yang sudah tercapai",
        count: passedIndicators,
        percentage: 0,
        color: "#48B920",
      },
      {
        key: "failed",
        label: "Indikator yang belum tercapai",
        count: failedIndicators,
        percentage: 0,
        color: "#CB494B",
      },
      {
        key: "unrated",
        label: "Indikator yang belum dinilai",
        count: pendingIndicators,
        percentage: 0,
        color: "#999999",
      },
    ];

    featuredCompetencies = [];
    statistic?.summary_materials?.map((s) => {
      if (s.periode == period) {
        s.indicators.map((i) => (i.category = s.material));
        indicators.push(...s.indicators);

        competencyTotals.indicators += s.total_indicator;
        passedIndicators += s.passed_indikator;
        failedIndicators += s.failed_indicator;
        pendingIndicators += s.pending_indicator;

        competencyProgress[0].count = passedIndicators;
        competencyProgress[1].count = failedIndicators;
        competencyProgress[2].count = pendingIndicators;

        competencyProgress[0].percentage = Math.ceil(
          competencyTotals.indicators
            ? (passedIndicators / competencyTotals.indicators) * 100
            : 0,
        );
        competencyProgress[1].percentage = Math.ceil(
          competencyTotals.indicators
            ? (failedIndicators / competencyTotals.indicators) * 100
            : 0,
        );
        competencyProgress[2].percentage = Math.ceil(
          competencyTotals.indicators
            ? (pendingIndicators / competencyTotals.indicators) * 100
            : 0,
        );

        featuredCompetencies.push({
          name: s.material,
          indicators: s.total_indicator,
          percentage: Math.ceil((s.passed_indikator * 100) / s.total_indicator),
        });
      }
    });
    indicators.sort((a, b) => b.value - a.value);
    if (indicators.length > 0) {
      competencyCarouselData = {
        positive: [{ name: indicators[0]?.name }],
        attention: [{ name: indicators[indicators.length - 1]?.name }],
      };
    }
    competencyTopIndicators = {
      positive: [],
      support: [],
    };
    indicators.forEach((i) => {
      if (i.value >= 2) {
        if (competencyTopIndicators.positive.length <= 2) {
          competencyTopIndicators.positive.push({
            category: i.category,
            label: i.label,
            name: i.name,
          });
        }
      } else {
        if (competencyTopIndicators.support.length <= 2) {
          competencyTopIndicators.support.push({
            category: i.category,
            label: i.label,
            name: i.name,
          });
        }
      }
    });
  }

  return (
    <View style={styles.content}>
      <View style={styles.card}>
        <SectionHeading
          Icon={BookIcon}
          title="Pencapaian Akademik"
          actionLabel="Lihat Semua"
        />
        <View style={styles.divider} />
        <SummaryTotalRow
          Icon={ProfileTickIcon}
          label="Total Kompetensi Unggulan"
          value={competencyTotals.competencies}
        />
        <SummaryTotalRow
          Icon={StarRoundIcon}
          label="Total Indikator"
          value={competencyTotals.indicators}
        />
        <View style={styles.progressDivider} />
        {competencyProgress.map((item, index) => (
          <ProgressStatusRow
            key={item.key}
            item={item}
            isLast={index === competencyProgress.length - 1}
          />
        ))}
      </View>

      <ReportGoodNewsCarousel
        indicatorData={competencyCarouselData}
        mode={"materials"}
      />

      <View style={styles.card}>
        <SectionHeading
          Icon={Heart2Icon}
          title="Kompetensi Unggulan"
          actionLabel="Lihat Semua"
        />
        <View style={styles.divider} />
        {featuredCompetencies.map((item) => (
          <CompetencyScoreRow key={item.name} item={item} />
        ))}
      </View>

      <CompetencyTopIndicators />
    </View>
  );
}

function ReflectionItem({ item, isLast }) {
  return (
    <View style={[styles.reflectionItem, isLast && styles.lastRow]}>
      <Text style={styles.reflectionTitle}>
        {item.title}:{" "}
        <Text style={styles.reflectionDescription}>{item.description}</Text>
      </Text>
      <View style={[styles.reflectionStatus, styles[item.tone]]}>
        <FlagIcon
          width={13}
          height={13}
          customColor={item.tone === "missing" ? "#D84343" : "#48B920"}
        />
        <Text
          style={[
            styles.statusText,
            item.tone === "missing" && styles.missingText,
          ]}
        >
          {item.label}
        </Text>
      </View>
    </View>
  );
}

function OtherContent({ statistic, period }) {
  reflections = [];
  otherMetrics = [];
  if (statistic) {
    competencyTotals.competencies = statistic?.materials?.length;
    competencyTotals.indicators = 0;
    statistic.summary_materials?.map((s) => {
      if (s.periode == period && s.material == "Pencapaian Lainnya") {
        // for others
        s.indicators.map((i) => {
          if (i.name.toLowerCase().indexOf("pilih nama") < 0) {
            otherMetrics.push({ label: i.name, value: i.value });
          }
        });
      }

      if (s.periode == period && s.material == "Refleksi Orangtua") {
        // for others
        s.indicators.map((i) => {
          reflections.push({
            title: i.name.split(":")[0],
            description: i.name.split(":")[1]?.trim() || "",
            label: i.label,
            tone: i.value <= 2 ? "missing" : "positive",
          });
        });
      }
    });
  }
  return (
    <View style={styles.content}>
      <View style={styles.card}>
        <SectionHeading
          Icon={Heart2Icon}
          title="Pencapaian Lainnya"
          actionLabel="Lihat Semua"
        />
        <View style={styles.divider} />
        {otherMetrics.map((item, index) => (
          <View
            key={item.label}
            style={[
              styles.otherMetricRow,
              index === otherMetrics.length - 1 && styles.lastRow,
            ]}
          >
            <Text style={styles.otherMetricLabel}>{item.label}</Text>
            <Text style={styles.otherMetricValue}>{item.value}</Text>
          </View>
        ))}
      </View>

      <View style={styles.card}>
        <SectionHeading Icon={Heart2Icon} title="Refleksi Orangtua" />
        <View style={styles.divider} />
        {reflections.map((item, index) => (
          <ReflectionItem
            key={item.title}
            item={item}
            isLast={index === reflections.length - 1}
          />
        ))}
      </View>
    </View>
  );
}

export default function ReportSecondaryCategoryContent({
  category,
  statistic,
  period,
}) {
  return category === "materials" ? (
    <CompetencyContent statistic={statistic} period={period} />
  ) : (
    <OtherContent statistic={statistic} period={period} />
  );
}

const styles = StyleSheet.create({
  content: { gap: 16 },
  card: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: "#FFFFFF",
    elevation: 2,
    shadowColor: "#34234A",
    shadowOpacity: 0.08,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
  },
  sectionHeading: {
    minHeight: 26,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  iconTile: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: "#F0E9FF",
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    flex: 1,
    color: "#171717",
    fontFamily: "Archivo-Bold",
    fontSize: 13,
  },
  actionBadge: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 18,
    backgroundColor: "#F0E9FF",
  },
  actionLabel: { color: "#6724E2", fontFamily: "Archivo-Regular", fontSize: 9 },
  divider: {
    height: 1,
    marginTop: 10,
    marginBottom: 5,
    backgroundColor: "#EEEEEE",
  },
  summaryRow: {
    minHeight: 47,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  summaryIcon: {
    width: 34,
    height: 34,
    borderRadius: 6,
    backgroundColor: "#000000",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryLabel: {
    flex: 1,
    color: "#171717",
    fontFamily: "Archivo-Regular",
    fontSize: 12,
  },
  summaryValue: {
    minWidth: 48,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: "#F0E9FF",
    color: "#5423B5",
    fontFamily: "Archivo-Bold",
    fontSize: 13,
    textAlign: "center",
  },
  progressDivider: {
    height: 1,
    marginTop: 8,
    marginBottom: 4,
    backgroundColor: "#E7E7E7",
  },
  progressRow: {
    minHeight: 58,
    paddingVertical: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#E5E5E5",
    borderStyle: "dashed",
  },
  lastProgressRow: { borderBottomWidth: 0 },
  progressStatusIcon: {
    width: 34,
    height: 34,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  progressCopy: { flex: 1, gap: 2 },
  progressLabel: {
    color: "#171717",
    fontFamily: "Archivo-Regular",
    fontSize: 11,
  },
  progressCount: { color: "#111111", fontFamily: "Archivo-Bold", fontSize: 14 },
  progressPercentage: {
    color: "#999999",
    fontFamily: "Archivo-Regular",
    fontSize: 13,
  },
  progressTrack: {
    width: 86,
    height: 10,
    borderRadius: 6,
    backgroundColor: "#F0F0F0",
    overflow: "hidden",
  },
  progressFill: { height: "100%", borderRadius: 6 },
  competencyScoreRow: {
    minHeight: 52,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  competencyCopy: { flex: 1 },
  competencyName: {
    color: "#5423B5",
    fontFamily: "Archivo-Bold",
    fontSize: 12,
  },
  competencyCount: {
    marginTop: 2,
    color: "#999999",
    fontFamily: "Archivo-Regular",
    fontSize: 11,
  },
  competencyScoreColumn: { width: "32%", alignItems: "flex-end", gap: 3 },
  competencyPercentage: {
    color: "#48B920",
    fontFamily: "Archivo-Bold",
    fontSize: 12,
  },
  competencyTrack: {
    width: "100%",
    height: 12,
    borderRadius: 6,
    backgroundColor: "#EAEAEA",
    overflow: "hidden",
  },
  competencyFill: {
    height: "100%",
    borderRadius: 6,
    backgroundColor: "#48B920",
  },
  competencySegments: {
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
    marginBottom: 4,
  },
  competencySegment: {
    position: "relative",
    minHeight: 52,
    flex: 1,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: "#E7E7E7",
    borderRadius: 9,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  bestSegmentActive: { borderColor: "#48B920" },
  supportSegmentActive: { borderColor: "#E24B4B" },
  competencySegmentText: {
    color: "#252525",
    fontFamily: "Archivo-Regular",
    fontSize: 11,
    lineHeight: 14,
  },
  bestSegmentText: { color: "#48B920" },
  supportSegmentText: { color: "#D84343" },
  segmentUnderline: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: 1,
    height: 4,
    borderRadius: 3,
  },
  bestUnderline: { backgroundColor: "#48B920" },
  supportUnderline: { backgroundColor: "#D84343" },
  topIndicatorRow: {
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#EEEEEE",
    borderStyle: "dashed",
  },
  lastTopIndicatorRow: { borderBottomWidth: 0 },
  topIndicatorChips: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  topCategoryChip: {
    minHeight: 24,
    paddingHorizontal: 8,
    borderRadius: 5,
    backgroundColor: "#F1F1F1",
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  topCategoryText: {
    color: "#555555",
    fontFamily: "Archivo-Regular",
    fontSize: 10,
  },
  topStatusChip: {
    minHeight: 24,
    paddingHorizontal: 8,
    borderRadius: 5,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  bestStatusChip: { backgroundColor: "#DDF3D5" },
  supportStatusChip: { backgroundColor: "#FFE0E0" },
  topStatusText: {
    color: "#48B920",
    fontFamily: "Archivo-Regular",
    fontSize: 10,
  },
  supportStatusText: { color: "#D84343" },
  topIndicatorDescription: {
    marginTop: 8,
    color: "#191919",
    fontFamily: "Archivo-Regular",
    fontSize: 11,
    lineHeight: 16,
  },
  lastRow: { borderBottomWidth: 0 },
  statusText: { color: "#48B920", fontFamily: "Archivo-Regular", fontSize: 9 },
  missingText: { color: "#D84343" },
  positive: { backgroundColor: "#DDF3D5" },
  missing: { backgroundColor: "#FFE0E0" },
  otherMetricRow: {
    minHeight: 39,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#EEEEEE",
    borderStyle: "dashed",
  },
  otherMetricLabel: {
    color: "#6724E2",
    fontFamily: "Archivo-Regular",
    fontSize: 10,
  },
  otherMetricValue: {
    color: "#48B920",
    fontFamily: "Archivo-Bold",
    fontSize: 10,
  },
  reflectionItem: {
    paddingVertical: 9,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#EEEEEE",
    borderStyle: "dashed",
  },
  reflectionTitle: {
    color: "#181818",
    fontFamily: "Archivo-Bold",
    fontSize: 10,
    lineHeight: 15,
  },
  reflectionDescription: { fontFamily: "Archivo-Regular" },
  reflectionStatus: {
    minHeight: 22,
    alignSelf: "flex-start",
    marginTop: 6,
    paddingHorizontal: 7,
    borderRadius: 5,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
});
